import { z } from 'zod';
import { CustomerClassificationEnum } from './customer';

export enum ConfigurationTypeEnum {
  CONTAINER_MATERIAL_MATRIX = 'CONTAINER_MATERIAL_MATRIX',
  CONTAINER_PERIOD_PRICING = 'CONTAINER_PERIOD_PRICING',
  PAY_AS_YOU_GO_PRICING = 'PAY_AS_YOU_GO_PRICING',
  TONNAGE_PRICING = 'TONNAGE_PRICING',
  AUTO_DISCOUNT = 'AUTO_DISCOUNT',
  DISCOUNT = 'DISCOUNT',
}
export type ConfigurationType =
  | ConfigurationTypeEnum
  | ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX
  | ConfigurationTypeEnum.CONTAINER_PERIOD_PRICING
  | ConfigurationTypeEnum.PAY_AS_YOU_GO_PRICING
  | ConfigurationTypeEnum.TONNAGE_PRICING
  | ConfigurationTypeEnum.AUTO_DISCOUNT
  | ConfigurationTypeEnum.DISCOUNT;

export const ConfigurationTypeSchema = z.enum([
  ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX,
  ConfigurationTypeEnum.CONTAINER_PERIOD_PRICING,
  ConfigurationTypeEnum.PAY_AS_YOU_GO_PRICING,
  ConfigurationTypeEnum.TONNAGE_PRICING,
  ConfigurationTypeEnum.AUTO_DISCOUNT,
  ConfigurationTypeEnum.DISCOUNT,
]);

export enum PricingTypeEnum {
  FIXED = 'fixed',
  PAY_AS_YOU_GO = 'payAsYouGo',
  BUNDLE = 'bundle',
  STANDARD = 'standard',
}

export type PricingType = PricingTypeEnum;
export type CustomerPricingModel = (typeof PricingTypeEnum)[keyof typeof PricingTypeEnum];

export const PricingTypeSchema = z.enum([
  PricingTypeEnum.FIXED,
  PricingTypeEnum.PAY_AS_YOU_GO,
  PricingTypeEnum.BUNDLE,
  PricingTypeEnum.STANDARD,
]);

export enum DiscountTypeEnum {
  PERCENTAGE = 'percent',
  FLAT = 'dollars',
}

export type DiscountType = DiscountTypeEnum;

export const DiscountTypeSchema = z.enum([DiscountTypeEnum.PERCENTAGE, DiscountTypeEnum.FLAT]);

export type MatrixData = z.infer<
  typeof ContainerMaterialMatrixDataSchema
>['matrixData'][string][string];

export const ContainerMaterialMatrixDataSchema = z.object({
  matrixData: z.record(
    z.string(),
    z.record(
      z.string(),
      z.object({
        enabled: z.boolean(),
        fixedPriceInCents: z.number().min(0).optional(),
        includedDays: z.number().min(1).optional(), // For fixed pricing - days included in flat rate
        dailyOverageFeeInCents: z.number().min(0).optional(), // For fixed pricing - daily overage rate (in cents)
      }),
    ),
  ),
});

export const TonnagePricingDataSchema = z.object({
  perTonRateInCents: z.number().min(0),
  minimumTonnage: z.number().min(0),
});

export const ContainerPeriodPricingDataSchema = z.object({
  containerPeriods: z.record(
    z.string(),
    z.object({
      basePriceInCents: z.number().min(0),
      includedTonnage: z.number().min(0),
      includedDays: z.number().min(1),
      additionalDayRateInCents: z.number().min(0),
      additionalTonRateInCents: z.number().min(0),
    }),
  ),
});

export const PayAsYouGoPricingDataSchema = z.object({
  containerRates: z.record(
    z.string(),
    z.object({
      dailyRateInCents: z.number().min(0),
      perTonRateInCents: z.number().min(0),
    }),
  ),
});

export const DiscountDataSchema = z.object({
  discountType: DiscountTypeSchema,
  discountPercent: z.number().min(0).max(100).optional(),
  discountAmountInCents: z.number().min(0).optional(),
});

export const ConfigurationDataSchema = z.union([
  ContainerMaterialMatrixDataSchema,
  ContainerPeriodPricingDataSchema,
  PayAsYouGoPricingDataSchema,
  TonnagePricingDataSchema,
  DiscountDataSchema,
]);

export const ConfigurationSchema = z.object({
  type: ConfigurationTypeSchema,
  data: ConfigurationDataSchema,
});

export const ScopeSchema = z.object({
  customerClassification: z
    .enum([
      CustomerClassificationEnum.INDIVIDUAL,
      CustomerClassificationEnum.CONTRACTOR,
      CustomerClassificationEnum.ALL,
    ])
    .optional(),
  zipCodes: z.union([z.array(z.string()), z.literal('all')]).optional(),
  customerIds: z.union([z.array(z.string()), z.literal('all')]).optional(),
  materialIds: z.array(z.string()).optional(),
});

export const PricingRuleSchema = z.object({
  _id: z.string().optional(),
  name: z.string().min(1),
  configuration: ConfigurationSchema,
  scope: ScopeSchema,
  precedence: z.number().min(100).max(1000),
  effectiveFrom: z.string().min(1),
  effectiveTo: z.string().min(1).optional(),
  canCombine: z.boolean(),
  organizationId: z.string(),
  active: z.boolean(),
  archived: z.boolean().optional(),
  version: z.number().optional(),
  createdBy: z.string().optional(),
  updatedBy: z.string().optional(),
});

export type TonnagePricingData = z.infer<typeof TonnagePricingDataSchema>;
export type ContainerPeriodPricingData = z.infer<typeof ContainerPeriodPricingDataSchema>;
export type PayAsYouGoPricingData = z.infer<typeof PayAsYouGoPricingDataSchema>;
export type ContainerMaterialMatrixData = z.infer<typeof ContainerMaterialMatrixDataSchema>;
export type DiscountData = z.infer<typeof DiscountDataSchema>;
export type ConfigurationData = z.infer<typeof ConfigurationDataSchema>;

// Base configuration interface with common fields
export interface BaseConfiguration {
  type: ConfigurationType;
}

// Specific configuration types that extend the base
export interface ContainerMaterialMatrixConfiguration extends BaseConfiguration {
  type: ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX;
  data: ContainerMaterialMatrixData;
}

export interface ContainerPeriodPricingConfiguration extends BaseConfiguration {
  type: ConfigurationTypeEnum.CONTAINER_PERIOD_PRICING;
  data: ContainerPeriodPricingData;
}

export interface PayAsYouGoPricingConfiguration extends BaseConfiguration {
  type: ConfigurationTypeEnum.PAY_AS_YOU_GO_PRICING;
  data: PayAsYouGoPricingData;
}

export interface TonnagePricingConfiguration extends BaseConfiguration {
  type: ConfigurationTypeEnum.TONNAGE_PRICING;
  data: TonnagePricingData;
}

export interface AutoDiscountConfiguration extends BaseConfiguration {
  type: ConfigurationTypeEnum.AUTO_DISCOUNT;
  data: DiscountData;
}

export interface DiscountConfiguration extends BaseConfiguration {
  type: ConfigurationTypeEnum.DISCOUNT;
  data: DiscountData;
}

// Discriminated union of all configuration types
export type Configuration =
  | ContainerMaterialMatrixConfiguration
  | ContainerPeriodPricingConfiguration
  | PayAsYouGoPricingConfiguration
  | TonnagePricingConfiguration
  | AutoDiscountConfiguration
  | DiscountConfiguration;

export type Scope = z.infer<typeof ScopeSchema>;
export type PricingRule = z.infer<typeof PricingRuleSchema>;
export type PricingRuleInput = z.infer<typeof PricingRuleSchema>;

// Form-specific type that is a proper subset of PricingRule
// Uses string for dates (HTML input format) instead of datetime
export interface PricingRuleFormData
  extends Omit<PricingRule, 'effectiveFrom' | 'effectiveTo' | 'createdAt' | 'updatedAt'> {
  effectiveFrom: string; // Date string for HTML input (YYYY-MM-DD)
  effectiveTo?: string; // Date string for HTML input (YYYY-MM-DD), optional for indefinite rules
}
